import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SeoHead from '@/components/feature/SeoHead';
import { catalogSections, categoriesBySection } from '@/mocks/catalog';
import { BLOG_ARTICLES, BLOG_CATEGORIES } from '@/mocks/blog';
import { pharmacies } from '@/mocks/pharmacies';
import { promoOffers } from '@/mocks/promos';

// ─── Slug helpers ─────────────────────────────────────────────────────────────
function toSlug(text: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
    з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
    п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  return text
    .toLowerCase()
    .split('')
    .map(c => map[c] ?? c)
    .join('')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Static pages ─────────────────────────────────────────────────────────────
const STATIC_PAGES = [
  { label: 'Главная', to: '/', icon: 'ri-home-4-line', color: '#ff2d78' },
  { label: 'Каталог', to: '/catalog', icon: 'ri-medicine-bottle-line', color: '#00f5ff' },
  { label: 'Акции и скидки', to: '/promos', icon: 'ri-price-tag-3-line', color: '#ff6b00' },
  { label: 'Программа лояльности', to: '/loyalty', icon: 'ri-vip-crown-line', color: '#f0ff00' },
  { label: 'Аптеки', to: '/pharmacies', icon: 'ri-store-2-line', color: '#39ff14' },
  { label: 'Доставка и оплата', to: '/delivery', icon: 'ri-truck-line', color: '#00f5ff' },
  { label: 'Блог о здоровье', to: '/blog', icon: 'ri-article-line', color: '#ff2d78' },
  { label: 'Поиск лекарств', to: '/search', icon: 'ri-search-line', color: '#c77dff' },
  { label: 'О компании', to: '/about', icon: 'ri-information-line', color: '#f0ff00' },
  { label: 'Контакты', to: '/contacts', icon: 'ri-phone-line', color: '#39ff14' },
];

// ─── Section colors ───────────────────────────────────────────────────────────
const SECTION_COLORS: Record<string, string> = {
  'Лекарства': '#ff2d78',
  'БАДы и витамины': '#f0ff00',
  'Дермакосметика': '#ff6b00',
  'Гомеопатия': '#39ff14',
  'Контактные линзы': '#00f5ff',
  'Медицинские изделия': '#c77dff',
  'Медицинские приборы': '#ff2d78',
};

// ─── Blog category colors ─────────────────────────────────────────────────────
const BLOG_CAT_COLORS: Record<string, string> = {
  immunity: '#39ff14',
  skincare: '#ff2d78',
  vitamins: '#f0ff00',
  heart: '#ff6b00',
  cold: '#00f5ff',
  sleep: '#c77dff',
  digestion: '#39ff14',
  children: '#ff2d78',
};

type TabId = 'all' | 'catalog' | 'blog' | 'pharmacies' | 'promos';

const TABS: { id: TabId; label: string; icon: string; color: string }[] = [
  { id: 'all', label: 'Все разделы', icon: 'ri-layout-grid-line', color: '#ff2d78' },
  { id: 'catalog', label: 'Каталог', icon: 'ri-medicine-bottle-line', color: '#00f5ff' },
  { id: 'blog', label: 'Блог', icon: 'ri-article-line', color: '#f0ff00' },
  { id: 'pharmacies', label: 'Аптеки', icon: 'ri-store-2-line', color: '#39ff14' },
  { id: 'promos', label: 'Акции', icon: 'ri-price-tag-3-line', color: '#ff6b00' },
];

// ─── Component ────────────────────────────────────────────────────────────────
const SitemapPage = () => {
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const totalUrls =
    STATIC_PAGES.length +
    catalogSections.length +
    Object.values(categoriesBySection).flat().length +
    BLOG_ARTICLES.length +
    pharmacies.length +
    promoOffers.length;

  const showAll = activeTab === 'all';

  return (
    <>
      <SeoHead
        title="Карта сайта NeonPharma — все разделы, статьи и аптеки"
        description="Полная карта сайта интернет-аптеки NeonPharma: каталог лекарств, витаминов и медицинских товаров, статьи блога о здоровье, адреса аптек и акции."
        keywords="карта сайта, NeonPharma, каталог лекарств, аптеки, блог здоровье"
        canonical="https://example.com/sitemap"
      />

      <Navbar />

      <main className="min-h-screen pt-20" style={{ background: '#05050a' }}>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="relative px-6 lg:px-12 py-16 overflow-hidden">
          {/* BG glows */}
          <div className="absolute top-0 left-1/4 w-96 h-48 opacity-10 rounded-full pointer-events-none" style={{ background: '#ff2d78', filter: 'blur(80px)' }} />
          <div className="absolute top-0 right-1/4 w-96 h-48 opacity-8 rounded-full pointer-events-none" style={{ background: '#00f5ff', filter: 'blur(80px)' }} />

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Breadcrumb */}
            <nav aria-label="Хлебные крошки" className="flex items-center gap-2 font-mono text-xs mb-8">
              <Link to="/" className="transition-colors" style={{ color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#00f5ff'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'}
              >Главная</Link>
              <i className="ri-arrow-right-s-line" style={{ color: 'rgba(255,255,255,0.2)' }} />
              <span style={{ color: '#ff2d78' }}>Карта сайта</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.25)' }}>
                    <i className="ri-map-2-line text-sm" style={{ color: '#ff2d78' }} />
                  </div>
                  <span className="font-mono text-xs tracking-widest" style={{ color: 'rgba(255,45,120,0.7)' }}>НАВИГАЦИЯ</span>
                </div>
                <h1 className="font-orbitron font-black text-3xl lg:text-4xl text-white mb-3" style={{ textShadow: '0 0 30px rgba(255,45,120,0.3)' }}>
                  КАРТА САЙТА
                </h1>
                <p className="font-mono text-sm max-w-lg" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Полная навигация по всем разделам, статьям и аптекам NeonPharma
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-4 flex-wrap">
                {[
                  { label: 'Всего страниц', value: totalUrls, color: '#ff2d78' },
                  { label: 'Разделов каталога', value: catalogSections.length, color: '#00f5ff' },
                  { label: 'Статей блога', value: BLOG_ARTICLES.length, color: '#f0ff00' },
                  { label: 'Аптек', value: pharmacies.length, color: '#39ff14' },
                ].map(stat => (
                  <div key={stat.label} className="px-4 py-3 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${stat.color}20` }}>
                    <div className="font-orbitron font-black text-2xl" style={{ color: stat.color, textShadow: `0 0 15px ${stat.color}60` }}>
                      {stat.value}
                    </div>
                    <div className="font-mono text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <section className="px-6 lg:px-12 pb-4 sticky top-[72px] z-30" style={{ background: 'rgba(5,5,10,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs whitespace-nowrap cursor-pointer transition-all duration-200"
                  style={{
                    background: activeTab === tab.id ? `${tab.color}18` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${activeTab === tab.id ? tab.color + '50' : 'rgba(255,255,255,0.08)'}`,
                    color: activeTab === tab.id ? tab.color : 'rgba(255,255,255,0.45)',
                    boxShadow: activeTab === tab.id ? `0 0 12px ${tab.color}25` : 'none',
                  }}
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="px-6 lg:px-12 py-10">
          <div className="max-w-7xl mx-auto space-y-10">

            {/* ── Static pages ─────────────────────────────────────────────── */}
            {(showAll) && (
              <section>
                <SectionHeader icon="ri-layout-grid-line" color="#ff2d78" title="Основные страницы" count={STATIC_PAGES.length} />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
                  {STATIC_PAGES.map(page => (
                    <Link
                      key={page.to}
                      to={page.to}
                      className="group flex flex-col items-center gap-2 p-4 rounded-xl text-center cursor-pointer transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.background = `${page.color}0d`;
                        (e.currentTarget as HTMLElement).style.borderColor = `${page.color}40`;
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)';
                      }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200" style={{ background: `${page.color}15`, border: `1px solid ${page.color}30` }}>
                        <i className={`${page.icon} text-lg`} style={{ color: page.color }} />
                      </div>
                      <span className="font-mono text-xs leading-tight" style={{ color: 'rgba(255,255,255,0.65)' }}>{page.label}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* ── Catalog ──────────────────────────────────────────────────── */}
            {(showAll || activeTab === 'catalog') && (
              <section>
                <SectionHeader icon="ri-medicine-bottle-line" color="#00f5ff" title="Каталог товаров" count={catalogSections.length + Object.values(categoriesBySection).flat().length} />
                <div className="mt-4 space-y-3">
                  {catalogSections.map(section => {
                    const slug = toSlug(section);
                    const color = SECTION_COLORS[section] ?? '#00f5ff';
                    const subcats = categoriesBySection[section] ?? [];
                    const isExpanded = expandedSection === section;

                    return (
                      <div key={section} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${color}20`, background: 'rgba(255,255,255,0.02)' }}>
                        {/* Section header row */}
                        <div className="flex items-center justify-between px-5 py-4">
                          <Link
                            to={`/catalog/section/${slug}`}
                            className="flex items-center gap-3 group flex-1 min-w-0"
                          >
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                              <i className="ri-medicine-bottle-line text-sm" style={{ color }} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-orbitron font-bold text-sm text-white group-hover:text-current transition-colors" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                {section}
                              </div>
                              <div className="font-mono text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                                {subcats.length} подкатегорий
                              </div>
                            </div>
                          </Link>
                          <div className="flex items-center gap-3 shrink-0">
                            <Link
                              to={`/catalog/section/${slug}`}
                              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] whitespace-nowrap cursor-pointer transition-all duration-200"
                              style={{ background: `${color}12`, border: `1px solid ${color}30`, color }}
                            >
                              <i className="ri-arrow-right-line" />
                              Перейти
                            </Link>
                            <button
                              onClick={() => setExpandedSection(isExpanded ? null : section)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
                              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}
                            >
                              <i className={`${isExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                            </button>
                          </div>
                        </div>

                        {/* Subcategories */}
                        {isExpanded && (
                          <div className="px-5 pb-4 pt-1 border-t" style={{ borderColor: `${color}15` }}>
                            <div className="flex flex-wrap gap-2">
                              {subcats.map(cat => (
                                <Link
                                  key={cat}
                                  to={`/catalog/section/${slug}?category=${toSlug(cat)}`}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200 whitespace-nowrap"
                                  style={{ background: `${color}08`, border: `1px solid ${color}20`, color: 'rgba(255,255,255,0.55)' }}
                                  onMouseEnter={e => {
                                    (e.currentTarget as HTMLElement).style.background = `${color}18`;
                                    (e.currentTarget as HTMLElement).style.color = color;
                                    (e.currentTarget as HTMLElement).style.borderColor = `${color}50`;
                                  }}
                                  onMouseLeave={e => {
                                    (e.currentTarget as HTMLElement).style.background = `${color}08`;
                                    (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)';
                                    (e.currentTarget as HTMLElement).style.borderColor = `${color}20`;
                                  }}
                                >
                                  <i className="ri-arrow-right-s-line text-xs" />
                                  {cat}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Blog ─────────────────────────────────────────────────────── */}
            {(showAll || activeTab === 'blog') && (
              <section>
                <SectionHeader icon="ri-article-line" color="#f0ff00" title="Блог о здоровье" count={BLOG_ARTICLES.length} />

                {/* Categories */}
                <div className="flex flex-wrap gap-2 mt-4 mb-5">
                  {BLOG_CATEGORIES.filter(c => c.slug !== 'all').map(cat => {
                    const color = BLOG_CAT_COLORS[cat.slug] ?? '#f0ff00';
                    return (
                      <Link
                        key={cat.slug}
                        to={`/blog?category=${cat.slug}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200 whitespace-nowrap"
                        style={{ background: `${color}10`, border: `1px solid ${color}25`, color: 'rgba(255,255,255,0.55)' }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.color = color;
                          (e.currentTarget as HTMLElement).style.borderColor = `${color}60`;
                          (e.currentTarget as HTMLElement).style.background = `${color}18`;
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)';
                          (e.currentTarget as HTMLElement).style.borderColor = `${color}25`;
                          (e.currentTarget as HTMLElement).style.background = `${color}10`;
                        }}
                      >
                        <i className="ri-bookmark-line text-xs" />
                        {cat.label}
                      </Link>
                    );
                  })}
                </div>

                {/* Articles grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {BLOG_ARTICLES.map(article => {
                    const color = BLOG_CAT_COLORS[article.categorySlug] ?? '#f0ff00';
                    return (
                      <Link
                        key={article.id}
                        to={`/blog/${article.slug}`}
                        className="group flex gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.background = `${color}08`;
                          (e.currentTarget as HTMLElement).style.borderColor = `${color}30`;
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                        }}
                      >
                        {/* Thumbnail */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${color}15`, color, border: `1px solid ${color}25` }}>
                              {article.category}
                            </span>
                            <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.25)' }}>{article.readTime} мин</span>
                          </div>
                          <p className="font-mono text-xs leading-snug line-clamp-2 transition-colors duration-200" style={{ color: 'rgba(255,255,255,0.65)' }}>
                            {article.title}
                          </p>
                          <div className="font-mono text-[9px] mt-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                            {new Date(article.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Pharmacies ───────────────────────────────────────────────── */}
            {(showAll || activeTab === 'pharmacies') && (
              <section>
                <SectionHeader icon="ri-store-2-line" color="#39ff14" title="Аптеки" count={pharmacies.length} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                  {pharmacies.map(pharmacy => (
                    <Link
                      key={pharmacy.id}
                      to={`/pharmacies/${pharmacy.id}`}
                      className="group p-4 rounded-xl cursor-pointer transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(57,255,20,0.12)' }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(57,255,20,0.05)';
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(57,255,20,0.35)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(57,255,20,0.12)';
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.25)' }}>
                          <i className="ri-store-2-line text-sm" style={{ color: '#39ff14' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-orbitron font-bold text-xs text-white mb-1 leading-tight">{pharmacy.name}</div>
                          <div className="font-mono text-[10px] mb-2 leading-snug" style={{ color: 'rgba(255,255,255,0.4)' }}>{pharmacy.address}</div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {pharmacy.is24h && (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.25)' }}>
                                24/7
                              </span>
                            )}
                            {pharmacy.hasDriveThru && (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,245,255,0.1)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.25)' }}>
                                Drive-thru
                              </span>
                            )}
                            <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                              <i className="ri-star-fill" style={{ color: '#f0ff00' }} /> {pharmacy.rating}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* ── Promos ───────────────────────────────────────────────────── */}
            {(showAll || activeTab === 'promos') && (
              <section>
                <SectionHeader icon="ri-price-tag-3-line" color="#ff6b00" title="Акции и спецпредложения" count={promoOffers.length} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  {promoOffers.map(promo => (
                    <Link
                      key={promo.id}
                      to={`/promos/${promo.id}`}
                      className="group relative overflow-hidden rounded-xl cursor-pointer transition-all duration-200"
                      style={{ border: `1px solid ${promo.color}25` }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.borderColor = `${promo.color}60`;
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.borderColor = `${promo.color}25`;
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Image */}
                      <div className="h-32 overflow-hidden relative">
                        <img src={promo.image} alt={promo.title} className="w-full h-full object-cover object-top" />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 30%, rgba(5,5,10,0.9))` }} />
                        <div className="absolute top-3 left-3">
                          <span className="font-orbitron font-black text-lg" style={{ color: promo.color, textShadow: `0 0 15px ${promo.color}` }}>
                            {promo.discount}
                          </span>
                        </div>
                        {promo.code && (
                          <div className="absolute top-3 right-3 px-2 py-1 rounded-full font-mono text-[9px]" style={{ background: 'rgba(0,0,0,0.6)', color: promo.color, border: `1px solid ${promo.color}40` }}>
                            {promo.code}
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="p-4" style={{ background: `${promo.color}06` }}>
                        <div className="font-mono text-[9px] mb-1" style={{ color: promo.color }}>{promo.badge}</div>
                        <div className="font-orbitron font-bold text-xs text-white leading-tight mb-1">{promo.title}</div>
                        <div className="font-mono text-[10px] line-clamp-2" style={{ color: 'rgba(255,255,255,0.4)' }}>{promo.subtitle}</div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Link to all promos */}
                <div className="mt-4 text-center">
                  <Link
                    to="/promos"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                    style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)', color: '#ff6b00' }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,107,0,0.18)';
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 0 15px rgba(255,107,0,0.25)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,107,0,0.1)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    <i className="ri-price-tag-3-line" />
                    Все акции и промокоды
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </section>
            )}

            {/* ── XML Sitemap link ─────────────────────────────────────────── */}
            {showAll && (
              <section>
                <div className="p-5 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.25)' }}>
                      <i className="ri-code-s-slash-line text-lg" style={{ color: '#00f5ff' }} />
                    </div>
                    <div>
                      <div className="font-orbitron font-bold text-sm text-white">XML Sitemap для поисковиков</div>
                      <div className="font-mono text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        Машиночитаемая карта сайта для Google и Яндекс
                      </div>
                    </div>
                  </div>
                  <a
                    href="/sitemap.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                    style={{ background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.18)';
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 0 12px rgba(0,245,255,0.25)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.1)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    <i className="ri-external-link-line" />
                    Открыть sitemap.xml
                  </a>
                </div>
              </section>
            )}

          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

// ─── Section header helper ────────────────────────────────────────────────────
interface SectionHeaderProps {
  icon: string;
  color: string;
  title: string;
  count: number;
}

const SectionHeader = ({ icon, color, title, count }: SectionHeaderProps) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: `${color}12`, border: `1px solid ${color}30` }}>
      <i className={`${icon} text-base`} style={{ color }} />
    </div>
    <div>
      <h2 className="font-orbitron font-bold text-base text-white">{title}</h2>
    </div>
    <span className="ml-auto font-mono text-xs px-2.5 py-1 rounded-full" style={{ background: `${color}10`, color, border: `1px solid ${color}25` }}>
      {count}
    </span>
    <div className="flex-1 h-px ml-2" style={{ background: `linear-gradient(90deg, ${color}30, transparent)` }} />
  </div>
);

export default SitemapPage;
